/* eslint-disable react-hooks/rules-of-hooks */
import { Typography } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import chroma from 'chroma-js';
import { FC, type JSX } from 'react';
import { AuthStatus, useAuth } from '../../auth';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';
import { FlexBox } from '../flex-box';
import { MenuButton } from './menu-button';
import { MenuItemProps } from './menu-item.types';
import { MenuLink } from './menu-link';

export const MenuItem: FC<MenuItemProps> = ({
  active,
  hasPermissions = true,
  handleClick,
  icon,
  keepAsDisabled = false,
  path,
  open,
  subText,
  sx,
  text,
  className,
  id,
  target = '_self',
  ...props
}) => {
  const { state } = useAuth();

  if (!state || state.status !== AuthStatus.AUTHENTICATED) return null;

  if (!hasPermissions && !keepAsDisabled) return <></>;

  const theme = useTheme();

  const textLength = innerText(text ?? '').length;
  const subTextLength = innerText(subText ?? '').length;

  const textStyles = useResponsiveText('1rem', textLength, {
    minFontSize: '0.875rem',
    maxFontSize: '1rem',
    textLengthThreshold: 20,
  });

  const subTextStyles = useResponsiveText('0.75rem', subTextLength, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 25,
  });

  const renderMenuItem = (): JSX.Element => (
    <FlexBox
      className="MenuItem-root"
      onClick={() => handleClick?.(path ?? '')}
      sx={{
        alignItems: 'stretch',
        flexDirection: 'row',
        height: 48,
        justifyItems: 'stretch',
        overflowX: 'hidden',
        cursor: !active ? 'pointer' : 'default',
        background: active ? chroma(theme.sidebar.background).darken(0.5).hex() : 'inherit',
        '.active &': { backgroundColor: chroma(theme.sidebar.background).darken(0.5).hex() },
        '&:hover': { backgroundColor: chroma(theme.sidebar.background).darken(0.5).hex() },
        ...sx,
      }}
      id={id}
      data-testid={props['data-testid']}
    >
      <FlexBox
        className="icon"
        sx={{
          alignItems: 'center',
          flexGrow: 0,
          flexShrink: 0,
          justifyContent: 'center',
        }}
      >
        <Box display='flex' alignItems='center' fontSize='1.5rem'>{icon}</Box>
      </FlexBox>
      <FlexBox
        className="text"
        sx={{
          alignItems: 'start',
          flexDirection: 'column',
          justifyContent: 'center',
          lineHeight: 1,
          opacity: open ? 1 : 0,
          overflow: 'hidden',
          p: 1,
          textOverflow: 'ellipsis',
          transition: '250ms opacity ease',
          whiteSpace: 'nowrap',
        }}
      >
        <Box
          sx={{
            mr: 2,
            fontSize: textStyles.fontSize,
            lineHeight: textStyles.lineHeight,
            wordBreak: textStyles.wordBreak,
          }}
        >
          {text}
        </Box>
        {subText && (
          <Typography
            variant='caption'
            mr={2}
            style={{
              fontSize: subTextStyles.fontSize,
              lineHeight: subTextStyles.lineHeight,
              wordBreak: subTextStyles.wordBreak,
            }}
          >
            {subText}
          </Typography>
        )}
      </FlexBox>
    </FlexBox>
  );

  if (handleClick || !path) return renderMenuItem();

  if (!hasPermissions) {
    return (
      <MenuButton data-testid={props['data-testid']} disabled>
        {renderMenuItem()}
      </MenuButton>
    );
  }

  return (
    <MenuLink data-testid={props['data-testid']} to={path} target={target} className={className}>
      {renderMenuItem()}
    </MenuLink>
  );
};
